/**
 * Theme (light/dark) preference. Persisted to localStorage — this is a
 * NON-SENSITIVE UI preference, which CLAUDE.md explicitly permits (unlike the
 * JWT, which is memory-only). Read once on load; the toggle lives in AppShell.
 */
import { create } from 'zustand';
import type { ThemeName } from '../theme';

const STORAGE_KEY = 'mmap.theme';

function readInitial(): ThemeName {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage unavailable (SSR/tests) — fall through */
  }
  return 'light';
}

function persist(mode: ThemeName): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export interface ThemeState {
  mode: ThemeName;
  toggle: () => void;
  set: (mode: ThemeName) => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: readInitial(),
  toggle: () => {
    const next: ThemeName = get().mode === 'dark' ? 'light' : 'dark';
    persist(next);
    set({ mode: next });
  },
  set: (mode) => {
    persist(mode);
    set({ mode });
  },
}));
